import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Drawer from '../components/Drawer';
import Modal from '../components/Modal';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { money, date } from '../lib/format';

const today = () => new Date().toISOString().slice(0, 10);
const FILTERS = [
  { key: 'pending', label: 'Review queue' },
  { key: 'posted', label: 'Posted' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];
const emptyForm = () => ({
  vendor: '', expense_date: today(), amount: '', tax_amount: '',
  is_mileage: false, miles: '',
  expense_account_id: '', payment_account_id: '', customer_id: '', job_id: '', billable: false,
  notes: '', receipt_file_url: '',
});

export default function Expenses() {
  const { activeId, setActiveId, businesses } = useBusiness();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = useAuth();
  const editable = can('expenses.manage');

  const [filter, setFilter] = useState('pending');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [reviewList, setReviewList] = useState([]);
  const [reviewJobs, setReviewJobs] = useState([]);
  const [assignSel, setAssignSel] = useState({});
  const [assignBusy, setAssignBusy] = useState(null);
  const [jobs, setJobs] = useState([]);

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null); // null = new
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [ocrEnabled, setOcrEnabled] = useState(false);
  const [mileageRate, setMileageRate] = useState(0.725);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState(null); // blob/obj URL for viewing
  const [acctModal, setAcctModal] = useState(null);   // inline "new account" quick-add
  const [drawerScanning, setDrawerScanning] = useState(false);
  const [drawerScanNote, setDrawerScanNote] = useState(null);

  const expenseAccounts = useMemo(() => accounts.filter((a) => a.type === 'expense'), [accounts]);
  const paymentAccounts = useMemo(() => accounts.filter((a) => a.is_bank || a.type === 'liability'), [accounts]);

  const receiptKind = useMemo(() => {
    const s = form.receipt_file_url || '';
    if (s.startsWith('data:image') || /\.(png|jpe?g|webp|gif)(\?|$)/i.test(s)) return 'image';
    if (s.startsWith('data:application/pdf') || /\.pdf(\?|$)/i.test(s)) return 'pdf';
    if (s.startsWith('data:text/html')) return 'html';
    return s ? 'other' : null;
  }, [form.receipt_file_url]);

  // Build a same-origin blob URL so images/PDFs (often stored as data: URIs) reliably open & embed.
  useEffect(() => {
    const src = open ? form.receipt_file_url : '';
    if (!src) { setReceiptUrl(null); return; }
    if (!src.startsWith('data:')) { setReceiptUrl(src); return; }
    try {
      const comma = src.indexOf(',');
      const mime = (src.slice(5, comma).split(';')[0]) || 'application/octet-stream';
      const bin = atob(src.slice(comma + 1));
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
      setReceiptUrl(url);
      return () => URL.revokeObjectURL(url);
    } catch { setReceiptUrl(src); }
  }, [open, form.receipt_file_url]);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api(`/expenses?business_id=${activeId}&status=${filter}`)
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch((e) => { setRows([]); if (e?.status && e.status !== 404) setError('Could not load expenses.'); })
      .finally(() => setLoading(false));
    api(`/expenses/summary?business_id=${activeId}`).then(setCounts).catch(() => setCounts({}));
  }, [activeId, filter]);

  const loadReview = useCallback(() => {
    api('/expenses/review').then((d) => setReviewList(Array.isArray(d) ? d : [])).catch(() => setReviewList([]));
    api('/expenses/review-jobs').then((d) => setReviewJobs(Array.isArray(d) ? d : [])).catch(() => setReviewJobs([]));
  }, []);
  useEffect(() => { loadReview(); }, [loadReview]);

  // Deep-link: ?open=<id> (e.g. clicking a receipt on a job) opens that expense, switching
  // to its business if needed so the list and pickers match.
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId) return;
    (async () => {
      try {
        const e = await api(`/expenses/${openId}`);
        if (e && e.business_id && Number(e.business_id) !== Number(activeId)) setActiveId(Number(e.business_id));
        setFilter('all');
        await openRow({ id: Number(openId) });
      } catch { /* not found — ignore */ }
      setSearchParams({}, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function deleteReceipt(expId) {
    if (!window.confirm('Delete this receipt? This removes the expense and its attached image.')) return;
    setAssignBusy(expId);
    try {
      await api(`/expenses/${expId}`, { method: 'DELETE' });
      setAssignSel((s) => { const n = { ...s }; delete n[expId]; return n; });
      loadReview();
      load();
    } catch (e) { setFormError(e?.detail || 'Could not delete that receipt.'); }
    finally { setAssignBusy(null); }
  }

  async function assignReceipt(expId) {
    const jobId = assignSel[expId];
    if (!jobId) return;
    setAssignBusy(expId);
    try {
      await api(`/expenses/${expId}/assign`, { method: 'POST', body: { job_id: Number(jobId) } });
      setAssignSel((s) => { const n = { ...s }; delete n[expId]; return n; });
      loadReview();
      load();
    } catch (e) { setFormError(e?.detail || 'Could not assign that receipt.'); }
    finally { setAssignBusy(null); }
  }

  async function saveQuickAccount() {
    const m = acctModal;
    if (!m || !m.name.trim()) { setAcctModal((mm) => ({ ...mm, error: 'Name is required.' })); return; }
    setAcctModal((mm) => ({ ...mm, saving: true, error: null }));
    try {
      const created = await api('/accounts', { method: 'POST', body: {
        business_id: activeId, type: m.type, name: m.name.trim(), is_bank: m.type === 'asset',
      } });
      setAccounts((prev) => [...prev, created].sort((a, b) => String(a.code).localeCompare(String(b.code))));
      setField(m.for === 'expense' ? 'expense_account_id' : 'payment_account_id', created.id);
      setAcctModal(null);
    } catch (e) {
      setAcctModal((mm) => ({ ...mm, saving: false, error: e?.detail || 'Could not create the account.' }));
    }
  }

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api(`/accounts?business_id=${activeId}`).then((d) => setAccounts(Array.isArray(d) ? d : [])).catch(() => setAccounts([]));
    api(`/customers?business_id=${activeId}`).then((d) => setCustomers((Array.isArray(d) ? d : []).filter((c) => c.is_active !== false))).catch(() => setCustomers([]));
    api(`/jobs?business_id=${activeId}`).then((d) => setJobs((Array.isArray(d) ? d : []).filter((j) => j.status !== 'cancelled'))).catch(() => setJobs([]));
    api('/expenses/ocr/status').then((s) => setOcrEnabled(!!s.enabled)).catch(() => setOcrEnabled(false));
    api('/expenses/settings').then((s) => { if (s?.mileage_rate) setMileageRate(Number(s.mileage_rate)); }).catch(() => {});
  }, [activeId]);

  function openNew() {
    setCurrent(null); setForm(emptyForm()); setFormError(null); setDrawerScanNote(null); setOpen(true);
  }

  async function onScanFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { setScanError('Receipt must be under 4 MB.'); return; }
    setScanError(null); setScanning(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(new Error('read failed'));
        r.readAsDataURL(file);
      });
      const created = await api('/expenses/scan', { method: 'POST', body: { business_id: activeId, file: dataUrl } });
      setFilter('pending');
      load();
      openRow(created); // open the scanned draft for review
    } catch (err) {
      setScanError(err?.detail || 'Could not scan that receipt. You can still add it manually.');
    } finally { setScanning(false); }
  }
  async function openRow(row) {
    setFormError(null); setDrawerScanNote(null); setOpen(true);
    try {
      const e = await api(`/expenses/${row.id}`);
      setCurrent(e);
      setForm({
        vendor: e.vendor || '', expense_date: e.expense_date?.slice(0, 10) || today(),
        amount: e.amount ?? '', tax_amount: e.tax_amount ?? '',
        is_mileage: e.miles != null, miles: e.miles ?? '',
        expense_account_id: e.expense_account_id || '', payment_account_id: e.payment_account_id || '',
        customer_id: e.customer_id || '', job_id: e.job_id || '', billable: !!e.billable, notes: e.notes || '',
        receipt_file_url: e.receipt_file_url || '',
      });
    } catch { setOpen(false); }
  }

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Move an expense (e.g. a receipt OCR'd into the wrong business) to another business.
  async function moveToBusiness(newBizId) {
    const target = Number(newBizId);
    if (!current || !target || target === Number(current.business_id)) return;
    setSaving(true); setFormError(null);
    try {
      await api(`/expenses/${current.id}/move`, { method: 'POST', body: { business_id: target } });
      setActiveId(target);              // page context follows the expense → reloads accounts/customers/jobs
      await openRow({ id: current.id }); // re-fetch the moved expense (account/customer/job now cleared)
    } catch (e) { setFormError(e?.detail || 'Could not move this expense.'); }
    finally { setSaving(false); }
  }
  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
  const mileageAmount = round2((Number(form.miles) || 0) * mileageRate);
  const total = form.is_mileage ? mileageAmount : (Number(form.amount) || 0) + (Number(form.tax_amount) || 0);
  const posted = current?.status === 'posted';

  function onReceiptFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { setFormError('Receipt must be under 4 MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => { setField('receipt_file_url', String(reader.result)); setDrawerScanNote(null); };
    reader.readAsDataURL(file);
  }

  // OCR the attached receipt and fill the form (works on manually-attached receipts too).
  async function scanAttached() {
    if (!form.receipt_file_url) return;
    setDrawerScanning(true); setDrawerScanNote(null); setFormError(null);
    try {
      const r = await api('/expenses/ocr/extract', { method: 'POST', body: { business_id: activeId, file: form.receipt_file_url } });
      setForm((f) => ({
        ...f,
        vendor: r.vendor || f.vendor,
        expense_date: r.expense_date || f.expense_date,
        amount: r.amount != null ? r.amount : f.amount,
        tax_amount: r.tax_amount != null ? r.tax_amount : f.tax_amount,
      }));
      const pct = r.confidence != null ? ` (${Math.round(r.confidence * 100)}% confidence)` : '';
      const names = (r.field_names && r.field_names.length) ? r.field_names.join(', ') : 'none';
      if (r.amount != null) {
        setDrawerScanNote(`Read with the ${r.model || 'OCR'} model${pct}. Review the fields, then pick a category and how it was paid.`);
      } else {
        setDrawerScanNote(`The ${r.model || 'OCR'} model ran but found no total${r.had_document === false ? ' — no document structure was recognized in the image' : ''}. Fields it detected: ${names}. Enter the amount manually.`);
      }
    } catch (e) { setFormError(e?.detail || 'Could not read that receipt.'); }
    finally { setDrawerScanning(false); }
  }

  function bodyFromForm() {
    return {
      business_id: activeId, vendor: form.vendor, expense_date: form.expense_date,
      amount: form.amount === '' ? null : Number(form.amount),
      tax_amount: form.tax_amount === '' ? 0 : Number(form.tax_amount),
      miles: form.is_mileage && form.miles !== '' ? Number(form.miles) : null,
      expense_account_id: form.expense_account_id || null,
      payment_account_id: form.payment_account_id || null,
      customer_id: form.customer_id || null, job_id: form.job_id || null, billable: !!form.billable,
      notes: form.notes || null, receipt_file_url: form.receipt_file_url || '',
    };
  }

  async function save({ thenPost } = {}) {
    setSaving(true); setFormError(null);
    try {
      let saved;
      if (current) saved = await api(`/expenses/${current.id}`, { method: 'PUT', body: bodyFromForm() });
      else saved = await api('/expenses', { method: 'POST', body: bodyFromForm() });
      if (thenPost) saved = await api(`/expenses/${saved.id}/post`, { method: 'POST' });
      setCurrent(saved);
      if (!thenPost) { setOpen(false); }
      else setForm((f) => ({ ...f })); // keep drawer, refresh status
      load();
      loadReview();
    } catch (e) { setFormError(e?.detail || 'Could not save the expense.'); }
    finally { setSaving(false); }
  }

  async function deleteCurrent() {
    if (!current) return;
    if (!window.confirm('Delete this expense? This removes it and its attached receipt.')) return;
    setSaving(true); setFormError(null);
    try {
      await api(`/expenses/${current.id}`, { method: 'DELETE' });
      setOpen(false);
      load();
      loadReview();
    } catch (e) { setFormError(e?.detail || 'Could not delete this expense.'); }
    finally { setSaving(false); }
  }

  async function action(verb) {
    if (!current) return;
    setSaving(true); setFormError(null);
    try {
      const updated = await api(`/expenses/${current.id}/${verb}`, { method: 'POST' });
      setCurrent(updated);
      if (verb === 'reject') setOpen(false);
      load();
      loadReview();
    } catch (e) { setFormError(e?.detail || `Could not ${verb} the expense.`); }
    finally { setSaving(false); }
  }

  const columns = [
    { key: 'expense_date', header: 'Date', render: (r) => (r.expense_date ? date(r.expense_date) : '—') },
    { key: 'vendor', header: 'Vendor', render: (r) => (
      <span>{r.vendor || (r.miles != null ? `Mileage · ${Number(r.miles)} mi` : '—')}{r.source === 'email' ? <span className="src-tag">email</span> : ''}</span>
    ) },
    { key: 'expense_name', header: 'Category', render: (r) => r.expense_name || <span className="hint">Uncategorized</span> },
    { key: 'customer_name', header: 'Bill to', render: (r) => (r.billable ? (r.customer_name || 'Billable') : '—') },
    { key: 'total', header: 'Total', render: (r) => <span className="num">{money(r.total)}</span> },
    { key: 'receipt', header: '', render: (r) => (r.has_receipt ? <span className="clip" title="Has receipt">📎</span> : null) },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  const drawerTitle = current ? (current.vendor || 'Expense') : 'New expense';
  const footer = (
    <>
      <button className="btn" onClick={() => setOpen(false)} disabled={saving}>Close</button>
      {editable && !posted && (
        <>
          <button className="btn" onClick={() => save()} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          <button className="btn btn--primary" onClick={() => save({ thenPost: true })} disabled={saving}>Save &amp; post</button>
        </>
      )}
      {editable && !posted && current && (
        <button className="btn btn--danger" onClick={deleteCurrent} disabled={saving}>Delete</button>
      )}
      {editable && posted && (
        <button className="btn btn--danger" onClick={() => action('unpost')} disabled={saving}>Unpost</button>
      )}
    </>
  );

  return (
    <>
      <PageHeader
        title="Expenses"
        actions={editable && (
          <div className="header-actions">
            {ocrEnabled && (
              <label className={'btn' + (scanning ? ' btn--busy' : '')}>
                {scanning ? 'Scanning…' : 'Scan receipt'}
                <input type="file" accept="image/*,application/pdf" onChange={onScanFile} hidden disabled={scanning} />
              </label>
            )}
            <button className="btn btn--primary" onClick={openNew}>Add expense</button>
          </div>
        )}
      />
      {scanError && <div className="notice notice--warn">{scanError}<button className="notice__x" onClick={() => setScanError(null)}>×</button></div>}

      {reviewList.length > 0 && (
        <section className="panel review-panel">
          <div className="review-panel__head">
            <strong>{reviewList.length} receipt{reviewList.length === 1 ? '' : 's'} to review</strong>
            <span className="hint">Emailed in with no matching job number. Assign each to a job (it moves to that job's business), or open it to file manually.</span>
          </div>
          <div className="review-list">
            {reviewList.map((r) => (
              <div className="review-row" key={r.id}>
                <div className="review-row__info">
                  <span className="review-row__vendor">{r.vendor || 'Unknown vendor'}</span>
                  <span className="hint">{money(r.total)} · {r.expense_date ? date(r.expense_date) : 'no date'} · filed under {r.short_code || r.business_name}</span>
                </div>
                <div className="review-row__actions">
                  <button type="button" className="btn btn--sm" onClick={() => openRow({ id: r.id })}>View receipt</button>
                  <select value={assignSel[r.id] || ''} onChange={(e) => setAssignSel((s) => ({ ...s, [r.id]: e.target.value }))}>
                    <option value="">Assign to job…</option>
                    {reviewJobs.map((j) => (
                      <option key={j.id} value={j.id}>{j.short_code} · {j.job_number || `#${j.id}`}{j.title ? ` — ${j.title}` : ''}</option>
                    ))}
                  </select>
                  <button type="button" className="btn btn--sm btn--primary" disabled={!assignSel[r.id] || assignBusy === r.id} onClick={() => assignReceipt(r.id)}>
                    {assignBusy === r.id ? 'Assigning…' : 'Assign'}
                  </button>
                  <button type="button" className="btn btn--sm btn--danger" disabled={assignBusy === r.id} onClick={() => deleteReceipt(r.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="filter-chips">
        {FILTERS.map((f) => (
          <button key={f.key} className={'chip' + (filter === f.key ? ' chip--on' : '')} onClick={() => setFilter(f.key)}>
            {f.label}{counts[f.key] != null && f.key !== 'all' ? ` (${counts[f.key]})` : ''}
          </button>
        ))}
      </div>

      <section className="panel">
        <DataTable columns={columns} rows={rows} loading={loading} error={error}
          getRowKey={(r) => r.id} onRowClick={openRow}
          empty={filter === 'pending' ? 'Nothing to review. Receipts captured by email or mobile will land here.' : 'No expenses here.'} />
      </section>

      <Drawer open={open} title={drawerTitle} width={680} onClose={() => !saving && setOpen(false)} footer={footer}>
        {formError && <div className="form-error">{formError}</div>}

        {current && (
          <div className="inv-status-row">
            <StatusBadge status={current.status} />
            {current.source && current.source !== 'manual' && <span className="hint">via {current.source}</span>}
            {posted && <span className="hint">Posted to the ledger — unpost to edit.</span>}
          </div>
        )}

        {current && (businesses || []).length > 1 && (
          !posted && !current.billed_invoice_id ? (
            <div className="field" style={{ marginTop: 10 }}>
              <label>Business</label>
              <select value={current.business_id} onChange={(e) => moveToBusiness(e.target.value)} disabled={saving}>
                {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <p className="hint">Moving to another business clears the category, payment account, customer, and job — they don't carry across.</p>
            </div>
          ) : (
            <p className="hint" style={{ marginTop: 10 }}>
              Business: <strong>{(businesses.find((b) => Number(b.id) === Number(current.business_id)) || {}).name || current.business_id}</strong>
              {posted ? ' — unpost to move it.' : current.billed_invoice_id ? ' — remove from its invoice to move it.' : ''}
            </p>
          )
        )}

        {current && current.ocr_confidence != null && !posted && (
          <div className={'scan-banner' + (Number(current.ocr_confidence) < 0.7 ? ' scan-banner--low' : '')}>
            Scanned from the receipt ({Math.round(Number(current.ocr_confidence) * 100)}% confidence).
            Check the fields below, then pick a category and how it was paid before posting.
          </div>
        )}

        <div className="form-grid">
          <div className="field"><label>Vendor</label>
            <input value={form.vendor} disabled={posted} onChange={(e) => setField('vendor', e.target.value)} placeholder="e.g. Amazon Web Services" /></div>
          <div className="field"><label>Date</label>
            <input type="date" value={form.expense_date} disabled={posted} onChange={(e) => setField('expense_date', e.target.value)} /></div>
        </div>

        <label className="check" style={{ marginBottom: 10 }}>
          <input type="checkbox" checked={form.is_mileage} disabled={posted}
            onChange={(e) => setField('is_mileage', e.target.checked)} />
          Mileage expense (calculate from miles at the IRS rate)
        </label>

        {form.is_mileage ? (
          <div className="form-grid">
            <div className="field"><label>Miles</label>
              <input type="number" step="0.1" min="0" value={form.miles} disabled={posted} autoFocus
                onChange={(e) => setField('miles', e.target.value)} placeholder="e.g. 124.5" /></div>
            <div className="field"><label>Rate</label>
              <input value={`$${mileageRate.toFixed(3)}/mi`} disabled readOnly /></div>
            <div className="field"><label>Amount (auto)</label>
              <input value={money(mileageAmount)} disabled readOnly /></div>
          </div>
        ) : (
          <div className="form-grid">
            <div className="field"><label>Amount (pre-tax)</label>
              <input type="number" step="0.01" value={form.amount} disabled={posted} onChange={(e) => setField('amount', e.target.value)} /></div>
            <div className="field"><label>Tax</label>
              <input type="number" step="0.01" value={form.tax_amount} disabled={posted} onChange={(e) => setField('tax_amount', e.target.value)} /></div>
            <div className="field"><label>Total</label>
              <input value={money(total)} disabled readOnly /></div>
          </div>
        )}
        {form.is_mileage && (
          <p className="hint" style={{ marginTop: -4 }}>{form.miles ? `${Number(form.miles)} mi × $${mileageRate.toFixed(3)} = ${money(mileageAmount)}` : ''} (current IRS business rate; tax not applicable)</p>
        )}

        <div className="form-grid">
          <div className="field"><label>Category (expense account)</label>
            <select value={form.expense_account_id} disabled={posted} onChange={(e) => setField('expense_account_id', e.target.value)}>
              <option value="">Select…</option>
              {expenseAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
            </select>
            {!posted && <button type="button" className="btn btn--sm add-acct" onClick={() => setAcctModal({ for: 'expense', type: 'expense', name: '', saving: false, error: null })}>+ New expense account</button>}
          </div>
          <div className="field"><label>Paid from{form.is_mileage ? ' (optional)' : ''}</label>
            <select value={form.payment_account_id} disabled={posted} onChange={(e) => setField('payment_account_id', e.target.value)}>
              <option value="">Select…</option>
              {paymentAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}{a.type === 'liability' ? ' (owed)' : ''}</option>)}
            </select>
            {!posted && <button type="button" className="btn btn--sm add-acct" onClick={() => setAcctModal({ for: 'payment', type: 'asset', name: '', saving: false, error: null })}>+ New bank / card</button>}
            {form.is_mileage && <p className="hint" style={{ marginTop: 6 }}>Leave blank for mileage — it offsets to Owner's Equity when posted.</p>}
          </div>
        </div>
        <p className="hint" style={{ marginTop: -4 }}>
          Pick a bank account if it's already paid, or Accounts Payable / a credit-card liability if you still owe it.
        </p>

        <div className="field"><label>Job (optional)</label>
          <select value={form.job_id} disabled={posted} onChange={(e) => {
            const jid = e.target.value;
            const job = jobs.find((j) => String(j.id) === String(jid));
            setForm((f) => ({
              ...f, job_id: jid,
              customer_id: job?.customer_id || f.customer_id,
              billable: job ? true : f.billable,
            }));
          }}>
            <option value="">No job</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.job_number} — {j.customer_name}{j.title ? ` · ${j.title}` : ''}</option>)}
          </select>
          {form.job_id && <p className="hint">Linked to this job; expense is billed to the job's customer.</p>}
        </div>

        <label className="checkrow">
          <input type="checkbox" checked={form.billable} disabled={posted} onChange={(e) => setField('billable', e.target.checked)} />
          <span>Billable to a customer (rebill on an invoice later).</span>
        </label>
        {form.billable && (
          <div className="field"><label>Customer</label>
            <select value={form.customer_id} disabled={posted} onChange={(e) => setField('customer_id', e.target.value)}>
              <option value="">Select a customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>)}
            </select>
          </div>
        )}

        <div className="field"><label>Notes</label>
          <textarea rows={2} value={form.notes} disabled={posted} onChange={(e) => setField('notes', e.target.value)} /></div>

        {/* Receipt */}
        <h3 className="form-section">Receipt</h3>
        {form.receipt_file_url ? (
          <div className="receipt">
            {receiptKind === 'image' && receiptUrl && (
              <a href={receiptUrl} target="_blank" rel="noreferrer" title="Open full size">
                <img src={receiptUrl} alt="receipt" className="receipt__img" />
              </a>
            )}
            {receiptKind === 'pdf' && receiptUrl && (
              <iframe src={receiptUrl} title="receipt" className="receipt__pdf" />
            )}
            {receiptKind === 'html' && receiptUrl && (
              <iframe src={receiptUrl} title="receipt" className="receipt__pdf" sandbox="" />
            )}
            {receiptKind === 'other' && (
              <p className="hint">Attached file — open it in a new tab to view.</p>
            )}
            <div className="receipt__actions">
              {receiptUrl && <a className="btn btn--sm" href={receiptUrl} target="_blank" rel="noreferrer">Open in new tab</a>}
              {ocrEnabled && !posted && receiptKind !== 'html' && (
                <button type="button" className="btn btn--sm" onClick={scanAttached} disabled={drawerScanning}>
                  {drawerScanning ? 'Reading…' : 'Scan with OCR'}
                </button>
              )}
              {!posted && <button type="button" className="btn btn--sm" onClick={() => { setField('receipt_file_url', ''); setDrawerScanNote(null); }}>Remove</button>}
            </div>
            {drawerScanNote && <p className="hint" style={{ marginTop: 8 }}>{drawerScanNote}</p>}
            {current && current.ocr_raw && (() => {
              let raw = current.ocr_raw;
              if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { raw = null; } }
              if (!raw || (!raw.model && !raw.text_snippet && !raw.detected && !raw.field_names)) return null;
              const detected = raw.detected || {};
              const detKeys = Object.keys(detected);
              return (
                <details className="ocr-diag">
                  <summary>OCR read details — what the scanner saw</summary>
                  <div className="ocr-diag__body">
                    <div><span className="k">Model:</span> {raw.model || '—'} · total source: {raw.total_source || '—'} · document recognized: {raw.had_document === false ? 'no' : 'yes'}</div>
                    <div><span className="k">Fields detected:</span> {detKeys.length ? detKeys.map((k) => `${k}=${detected[k]}`).join('  ·  ') : ((raw.field_names || []).join(', ') || 'none')}</div>
                    <div className="k" style={{ marginTop: 6 }}>Text Azure read:</div>
                    <pre className="ocr-diag__text">{raw.text_snippet || '(no text was returned — the image/PDF likely could not be read)'}</pre>
                  </div>
                </details>
              );
            })()}
          </div>
        ) : (
          !posted && (
            <label className="btn btn--sm">
              Attach receipt
              <input type="file" accept="image/*,application/pdf" onChange={onReceiptFile} hidden />
            </label>
          )
        )}

        {/* secondary actions for a pending/rejected expense */}
        {editable && current && !posted && (
          <div className="quote-actions">
            {current.status !== 'rejected' && <button className="btn btn--sm btn--danger" onClick={() => action('reject')} disabled={saving}>Reject</button>}
          </div>
        )}
      </Drawer>

      <Modal
        open={!!acctModal}
        title="New account"
        onClose={() => { if (!acctModal?.saving) setAcctModal(null); }}
        footer={<>
          <button className="btn" onClick={() => setAcctModal(null)} disabled={acctModal?.saving}>Cancel</button>
          <button className="btn btn--primary" onClick={saveQuickAccount} disabled={acctModal?.saving}>{acctModal?.saving ? 'Creating…' : 'Create & select'}</button>
        </>}
      >
        {acctModal && (
          <>
            {acctModal.error && <div className="form-error">{acctModal.error}</div>}
            {acctModal.for === 'payment' && (
              <div className="field"><label>Account type</label>
                <select value={acctModal.type} onChange={(e) => setAcctModal({ ...acctModal, type: e.target.value })}>
                  <option value="asset">Bank account</option>
                  <option value="liability">Credit card / line of credit</option>
                </select>
              </div>
            )}
            <div className="field"><label>Name</label>
              <input autoFocus value={acctModal.name}
                onChange={(e) => setAcctModal({ ...acctModal, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') saveQuickAccount(); }}
                placeholder={acctModal.for === 'expense' ? 'e.g. Travel & Airfare' : acctModal.type === 'liability' ? 'e.g. Amex Business Card' : 'e.g. Operating Checking'} />
            </div>
            <p className="hint">A code is assigned automatically. You can rename or renumber accounts under Chart of accounts.</p>
          </>
        )}
      </Modal>
    </>
  );
}
